import type { WorkspaceSession } from "./workspaceSession";
import type {
  Draft,
  FeedbackCategory,
  FeedbackContext,
  FeedbackExperience,
  Submission,
  Workspace,
  WritingEvent,
} from "@context/shared";

/** A structured failure from the service, carrying the message to show the user. */
export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** Reads any session already stored in this browser. */
  loadSession?: () => WorkspaceSession | null;
  /** Persists a newly minted session. */
  saveSession?: (session: WorkspaceSession) => void;
  /** Discards a session the service has stopped accepting. */
  dropSession?: () => void;
}

/** The payload accepted by the public feedback endpoint. */
export interface SubmitFeedbackInput {
  message: string;
  category: FeedbackCategory;
  experience?: FeedbackExperience | null;
  name?: string | null;
  email?: string | null;
  context?: FeedbackContext;
}

export interface SubmitFeedbackResult {
  id: string;
  createdAt: string;
}

/** Per-call override for the request ceiling, plus an external abort signal. */
export interface RequestOptions {
  /**
   * Lets a caller cancel the request (e.g. the submit drain cancelling a stuck
   * pre-submit sync). The per-request ceiling below still applies on top, so a
   * caller that never aborts cannot leave a request hanging either.
   */
  signal?: AbortSignal;
}

/**
 * Ceiling on every request. ApiClient has no other bound, so a request parked
 * on a blackholed connection would otherwise hang the caller forever — submit
 * or a flush included. Each request carries this as an AbortSignal that fires
 * after the window; a persistent outage then surfaces a network error instead
 * of freezing the UI.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * A draft sync body carries the whole snapshot (html + text + metrics) and a
 * flush batch can hold up to 200 events, so those are the app's large payloads.
 * They get a generous-but-bounded ceiling rather than the default.
 */
const LARGE_BODY_TIMEOUT_MS = 60_000;

/** The PDF report is a bigger binary over a possibly slow link. */
const PDF_DOWNLOAD_TIMEOUT_MS = 120_000;

const DEFAULT_BASE_URL = "/api/v1";

/**
 * Browsers reject keepalive fetches whose request body exceeds 64 KiB with a
 * TypeError, and the fetch spec caps the SUM of all in-flight keepalive
 * bodies at 64 KiB. Leave headroom below the per-body cap so a pagehide flush
 * can keep carrying keepalive while larger bodies fall back to a normal fetch.
 * flushNow fires syncDraft and flushEvents concurrently, so only the events
 * POST (the small, irreplaceable payload) carries keepalive — see below.
 */
const KEEPALIVE_MAX_BODY_BYTES = 60 * 1024;

const textEncoder = new TextEncoder();

/**
 * The keepalive cap is byte-based (UTF-8), not code-unit-based: `body.length`
 * counts UTF-16 code units, so a 60K-char CJK/emoji draft (~180 KB UTF-8)
 * would pass a length gate and still exceed the cap. Measure the encoded
 * bytes so the gate matches what the browser actually enforces.
 */
function fitsKeepalive(body: string): boolean {
  return textEncoder.encode(body).length <= KEEPALIVE_MAX_BODY_BYTES;
}

/**
 * True when a request was cancelled rather than failing on its own — a caller
 * (the submit drain) superseding a stuck sync. The superseded path should stay
 * silent instead of surfacing "try again": the flow that cancelled it reports
 * its own outcome.
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof ApiClientError && error.code === "request_aborted";
}

/** The timeout ceiling aborts with the same reason the fetch spec uses. */
function timeoutReason(): DOMException {
  return new DOMException("The request timed out.", "TimeoutError");
}

interface BoundedSignal {
  signal: AbortSignal;
  /** True when the CALLER's signal aborted the request (not the ceiling). */
  abortedExternally: boolean;
  clear: () => void;
}

/**
 * Combines a caller-supplied abort signal (if any) with the per-request
 * timeout ceiling. Without a caller signal the browser's `AbortSignal.timeout`
 * bounds the request directly; with one, the caller's abort is forwarded to an
 * internal controller that also carries the same ceiling, so every request is
 * bounded no matter which path reaches the wire.
 */
function boundSignal(external: AbortSignal | null | undefined, timeoutMs: number): BoundedSignal {
  if (!external) {
    return { signal: AbortSignal.timeout(timeoutMs), abortedExternally: false, clear: () => {} };
  }

  const controller = new AbortController();
  let abortedExternally = false;
  const onExternalAbort = () => {
    abortedExternally = true;
    controller.abort(external.reason);
  };
  if (external.aborted) {
    onExternalAbort();
  } else {
    external.addEventListener("abort", onExternalAbort, { once: true });
  }
  const timer = setTimeout(() => controller.abort(timeoutReason()), timeoutMs);
  return {
    signal: controller.signal,
    // Live, not a snapshot: an external abort can land any time (headers OR
    // body phase) and must flip this flag even after boundSignal has returned,
    // so perform's catch can tell a caller's cancellation from the ceiling.
    get abortedExternally() {
      return abortedExternally;
    },
    clear: () => {
      clearTimeout(timer);
      external.removeEventListener("abort", onExternalAbort);
    },
  };
}

/**
 * Thin typed wrapper over the ConText service. Every call is a student call and
 * carries the opaque workspace id minted by the service.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private session: WorkspaceSession | null = null;
  /** In-flight session request, so concurrent calls mint only one workspace. */
  private sessionPromise: Promise<WorkspaceSession> | null = null;

  constructor(private readonly options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? DEFAULT_BASE_URL;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.session = options.loadSession?.() ?? null;
  }

  /** The current workspace id, once a session exists. Diagnostics only. */
  get workspaceId(): string | null {
    return this.session?.workspaceId ?? null;
  }

  /**
   * Returns the stored session, asking the service for one the first time.
   * The id is chosen by the server; the client never proposes it.
   */
  private async ensureSession(): Promise<WorkspaceSession> {
    if (this.session) return this.session;
    if (this.sessionPromise) return this.sessionPromise;

    this.sessionPromise = (async () => {
      const created = await this.rawRequest<WorkspaceSession>("/workspace/session", {
        method: "POST",
      });
      this.session = created;
      this.options.saveSession?.(created);
      return created;
    })();

    try {
      return await this.sessionPromise;
    } finally {
      this.sessionPromise = null;
    }
  }

  /**
   * Sends a request carrying the workspace token, minting a session first if
   * needed. A rejected token is discarded and the call retried once, so a
   * restarted service (which regenerates its signing key) recovers on its own
   * instead of leaving the app permanently broken.
   */
  private async request<T>(
    path: string,
    init: RequestInit = {},
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ): Promise<T> {
    await this.ensureSession();

    try {
      return await this.rawRequest<T>(path, init, timeoutMs);
    } catch (error) {
      const rejected =
        error instanceof ApiClientError &&
        error.status === 401 &&
        error.code === "workspace_unauthorized";

      if (!rejected) throw error;

      this.session = null;
      this.options.dropSession?.();
      await this.ensureSession();
      return this.rawRequest<T>(path, init, timeoutMs);
    }
  }

  /** Same token-and-retry handling as {@link request}, but for binary bodies. */
  private async requestBlob(
    path: string,
    init: RequestInit = {},
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ): Promise<Blob> {
    await this.ensureSession();

    try {
      return await this.rawBlob(path, init, timeoutMs);
    } catch (error) {
      const rejected =
        error instanceof ApiClientError &&
        error.status === 401 &&
        error.code === "workspace_unauthorized";

      if (!rejected) throw error;

      this.session = null;
      this.options.dropSession?.();
      await this.ensureSession();
      return this.rawBlob(path, init, timeoutMs);
    }
  }

  private async rawRequest<T>(
    path: string,
    init: RequestInit = {},
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ): Promise<T> {
    return this.perform(path, init, timeoutMs, (response) => response.json() as Promise<T>);
  }

  private async rawBlob(
    path: string,
    init: RequestInit = {},
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ): Promise<Blob> {
    return this.perform(path, init, timeoutMs, (response) => response.blob());
  }

  private async perform<T>(
    path: string,
    init: RequestInit,
    timeoutMs: number,
    readBody: (response: Response) => Promise<T>,
  ): Promise<T> {
    const headers = new Headers(init.headers);
    if (this.session) {
      headers.set("X-Workspace-Token", `Workspace ${this.session.token}`);
    }
    if (init.body !== undefined) {
      headers.set("Content-Type", "application/json");
    }

    // The ceiling stays armed across the WHOLE exchange — headers AND body. A
    // server that sends headers then stalls mid-body must hit the same bound as
    // one that never answers at all, so the signal is kept alive until the body
    // read below settles and only cleared in the finally. Clearing it when the
    // fetch resolves would leave response.json()/blob() unbounded again.
    const bounded = boundSignal(init.signal, timeoutMs);
    try {
      const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        headers,
        signal: bounded.signal,
      });

      if (response.status === 204) {
        return undefined as T;
      }

      if (!response.ok) {
        const payload = await this.readErrorPayload(response, bounded);
        const error = (payload as { error?: { code?: string; message?: string; details?: unknown } })
          ?.error;
        throw new ApiClientError(
          response.status,
          error?.code ?? "unknown_error",
          error?.message ?? `Request failed with status ${response.status}.`,
          error?.details,
        );
      }

      return await new Promise<T>((resolve, reject) => {
        const onAbort = () => {
          const reason =
            bounded.signal.reason instanceof Error ? bounded.signal.reason : new Error("Aborted");
          reject(reason);
        };
        if (bounded.signal.aborted) {
          onAbort();
          return;
        }
        bounded.signal.addEventListener("abort", onAbort, { once: true });
        readBody(response).then(
          (value) => {
            bounded.signal.removeEventListener("abort", onAbort);
            resolve(value);
          },
          (cause) => {
            bounded.signal.removeEventListener("abort", onAbort);
            reject(cause);
          },
        );
      });
    } catch (cause) {
      if (cause instanceof ApiClientError) throw cause;
      // An abort that lands in the body phase classifies exactly like one in the
      // header phase: a caller's cancellation is silently swallowed downstream
      // (request_aborted), while the ceiling firing is a reportable outage.
      if (bounded.signal.aborted && bounded.abortedExternally) {
        throw new ApiClientError(0, "request_aborted", "This request was cancelled.", cause);
      }
      throw new ApiClientError(
        0,
        "network_error",
        "Could not reach the ConText service. Check your connection and try again.",
        cause,
      );
    } finally {
      bounded.clear();
    }
  }

  /**
   * Parses a non-ok response's error body. A body that is merely unreadable
   * (e.g. an HTML error page) is not worth failing over: the status already
   * tells the caller the request failed. But when the ceiling or the caller's
   * cancel aborts mid-body, the abort is rethrown so the surrounding perform
   * classifies it exactly like a header-phase one.
   */
  private async readErrorPayload(response: Response, bounded: BoundedSignal): Promise<unknown> {
    try {
      return await response.json();
    } catch (cause) {
      if (bounded.signal.aborted) throw cause;
      return null;
    }
  }

  /* ---------------------------------------------------------------- workspace */

  getWorkspace(): Promise<Workspace> {
    return this.request<Workspace>("/workspace");
  }

  updateReferencePrompt(
    referencePrompt: string,
    options: RequestOptions = {},
  ): Promise<{ referencePrompt: string }> {
    return this.request("/workspace/reference-prompt", {
      method: "PUT",
      body: JSON.stringify({ referencePrompt }),
      signal: options.signal,
    });
  }

  /* ------------------------------------------------------------------- drafts */

  syncDraft(
    patch: {
      html?: string;
      text?: string;
      professorEmail?: string;
      studentEmail?: string;
      trackingActive?: boolean;
      metrics?: Partial<Draft["metrics"]>;
      activity?: Partial<Draft["activity"]>;
    },
    options: RequestOptions = {},
  ): Promise<Draft> {
    const body = JSON.stringify(patch);
    // No keepalive here. flushNow fires syncDraft and flushEvents concurrently
    // on tab close, and the fetch spec caps the SUM of all in-flight keepalive
    // bodies at 64 KiB — a large draft PUT next to an events POST could trip
    // the combined cap and reject BOTH. Only the events POST carries keepalive
    // (the small, irreplaceable behavioral payload); the draft rides the
    // visibilitychange flush that normally runs before a close, and the
    // autosave debounce keeps the server near-current.
    return this.request<Draft>(
      "/drafts/current",
      {
        method: "PUT",
        body,
        signal: options.signal,
      },
      LARGE_BODY_TIMEOUT_MS,
    );
  }

  startSession(): Promise<Draft> {
    return this.request<Draft>("/drafts/current/session", { method: "POST" });
  }

  appendEvents(events: WritingEvent[], options: RequestOptions = {}): Promise<{ eventCount: number }> {
    const body = JSON.stringify({ events });
    // Keepalive lets a pagehide flush (the only flush running on tab close)
    // actually reach the service instead of being aborted by the browser.
    // This is the ONLY keepalive body in flight: syncDraft carries none, so
    // the combined 64 KiB in-flight cap cannot be tripped. The per-body cap is
    // byte-measured (fitsKeepalive) so a CJK/emoji-heavy batch does not sneak
    // past a code-unit count; batches over the cap omit the flag and accept
    // the tiny tab-close loss window rather than failing the flush.
    const keepalive = fitsKeepalive(body);
    return this.request(
      "/drafts/current/events",
      {
        method: "POST",
        ...(keepalive ? { keepalive } : {}),
        body,
        signal: options.signal,
      },
      LARGE_BODY_TIMEOUT_MS,
    );
  }

  /* ---------------------------------------------------------------- feedback */

  /**
   * Public general feedback. This deliberately does NOT call ensureSession:
   * the feedback tab must work without a workspace (including when loading the
   * workspace failed). If this browser already has a session, perform still
   * attaches its token, which the endpoint ignores.
   */
  submitFeedback(
    payload: SubmitFeedbackInput,
    options: RequestOptions = {},
  ): Promise<SubmitFeedbackResult> {
    return this.rawRequest<SubmitFeedbackResult>("/feedback", {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options.signal,
    });
  }

  /* -------------------------------------------------------------- submissions */

  submitDraft(): Promise<{ submission: Submission; draft: Draft }> {
    return this.request("/submissions", { method: "POST" });
  }

  /** Fetches the stored PDF report for one of this workspace's submissions. */
  downloadSubmissionPdf(submissionId: string): Promise<Blob> {
    return this.requestBlob(
      `/workspace/submissions/${submissionId}/pdf`,
      {},
      PDF_DOWNLOAD_TIMEOUT_MS,
    );
  }
}
